import { InputAutosuggest, FormField, Paragraph } from '@vwfs-bronson/bronson-react';
import { ChangeAddress } from 'cpfr-common';
import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { CpDataApi } from 'cp-xhr';
import { useTranslation } from 'react-i18next';

export type AddressSuggestion = {
    id: string;
    label: string;
    street?: string;
    houseNumber?: string;
    zipCode?: string;
    city?: string;
    countryName?: string;
    resultType?: string;
};

const DEBOUNCE_MS = 200;
const MIN_CHARS = 3;

const buildAddressLabel = (addr: Partial<AddressSuggestion>): string =>
    [addr.street, addr.houseNumber, addr.zipCode, addr.city, addr.countryName].filter(Boolean).join(', ');

export const AddressesSuggestionsInput = () => {
    const { t } = useTranslation('my-profile');
    const { setFieldValue, values } = useFormikContext<ChangeAddress>();

    const [items, setItems] = useState<AddressSuggestion[]>([]);
    const [suggestionLabels, setSuggestionLabels] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPosition, setCurrentPosition] = useState('48.8566,2.3522');

    const timeoutRef = useRef<number>();
    const requestIdRef = useRef(0);
    const isSelectingRef = useRef(false);

    const startsWithNumber = (value: string) => /^\d/.test(value);

    useEffect(() => {
        const prefetchQuery = encodeURIComponent(' ');
        CpDataApi.get('/address-suggestions', {
            params: { query: prefetchQuery, at: encodeURIComponent(currentPosition) },
            // eslint-disable-next-line @typescript-eslint/no-empty-function
        }).catch(() => {});
    }, [currentPosition]);

    useEffect(() => {
        if (currentPosition === '48.8566,2.3522' && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
                setCurrentPosition(`${pos.coords.latitude},${pos.coords.longitude}`);
            });
        }
    }, []);

    useEffect(() => {
        if (isSelectingRef.current) {
            isSelectingRef.current = false;
            return;
        }

        const query = searchQuery.trim();
        if (!query) {
            setItems([]);
            setSuggestionLabels([]);
            return;
        }

        const shouldSearch = query.length >= MIN_CHARS || startsWithNumber(query);
        if (!shouldSearch) return;

        window.clearTimeout(timeoutRef.current);

        timeoutRef.current = window.setTimeout(() => {
            const reqId = ++requestIdRef.current;

            CpDataApi.get('/address-suggestions', {
                params: { query: encodeURIComponent(query), at: encodeURIComponent(currentPosition) },
            })
                .then(({ data }) => {
                    if (reqId !== requestIdRef.current) return;

                    const mapped: AddressSuggestion[] = (Array.isArray(data) ? data : []).map(
                        (addr: AddressSuggestion) => {
                            const label =
                                addr.resultType === 'street' || addr.street
                                    ? buildAddressLabel({
                                          street: addr.street,
                                          houseNumber: addr.houseNumber,
                                          zipCode: addr.zipCode,
                                          city: addr.city,
                                          countryName: addr.countryName,
                                      })
                                    : addr.label;

                            return {
                                id: String(addr.id ?? label),
                                label,
                                street: addr.street,
                                houseNumber: addr.houseNumber,
                                zipCode: addr.zipCode,
                                city: addr.city,
                                countryName: addr.countryName,
                                resultType: addr.resultType,
                            };
                        },
                    );

                    const completePostalCodeMatch = query.match(/^(\d{5})\b/);
                    const onlyPostalCode = /^\d{1,5}$/.test(query);
                    const postalPrefix = query.match(/^\d+/)?.[0] ?? '';

                    const filtered = completePostalCodeMatch
                        ? mapped.filter((a) => a.zipCode === completePostalCodeMatch[1])
                        : onlyPostalCode && postalPrefix
                        ? mapped.filter((a) => a.zipCode?.startsWith(postalPrefix))
                        : mapped;

                    const currentPostalCode = values.postalCode?.trim() ?? '';
                    const sorted = currentPostalCode
                        ? [...filtered].sort((a, b) => {
                              const aMatches = a.zipCode?.startsWith(currentPostalCode) ? -1 : 0;
                              const bMatches = b.zipCode?.startsWith(currentPostalCode) ? -1 : 0;
                              return aMatches - bMatches;
                          })
                        : filtered;

                    setItems(sorted);
                    setSuggestionLabels(sorted.map((i) => i.label));
                })
                .catch(() => {
                    setItems([]);
                    setSuggestionLabels([]);
                });
        }, DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutRef.current);
    }, [searchQuery, currentPosition]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleSelect = (e: CustomEvent) => {
        const selectedLabel = (e.target as HTMLInputElement).value;
        if (!selectedLabel) return;

        const match = items.find((i) => i.label === selectedLabel);
        if (!match) return;

        setFieldValue('street', match.street ?? '');
        setFieldValue('houseNumber', match.houseNumber ?? '');
        setFieldValue('postalCode', match.zipCode ?? '');
        setFieldValue('city', match.city ?? '');
        setFieldValue('country', match.countryName ?? '');

        isSelectingRef.current = true;
        setSearchQuery(match.label);

        setItems([]);
        setSuggestionLabels([]);
    };

    return (
        <FormField
            type="autosuggest"
            labelText={t('address.edit-view.input-suggestions.label')}
            className="c-form-field--no-floating u-mt-small"
        >
            <Paragraph className="c-form-field__label">{t('address.edit-view.input-suggestions.text')}</Paragraph>
            <InputAutosuggest
                id="address-autosuggest"
                type="text"
                value={searchQuery}
                list={suggestionLabels}
                awesompleteProps={{
                    list: suggestionLabels,
                    listLabel: 'addresses suggestions',
                    maxItems: 10,
                    minChars: MIN_CHARS,
                    filter: () => true,
                }}
                onChange={handleChange}
                onSelectComplete={handleSelect}
            />
        </FormField>
    );
};
